import { useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text, makeStyles, tokens } from '@fluentui/react-components';
import { ChevronDownRegular, ChevronRightRegular, InfoRegular } from '@fluentui/react-icons';
import {
  browserStorage,
  readCollapsed,
  stepsInOrder,
  writeCollapsed,
  type InstructionScreen,
} from '../lib/instructions';

const useStyles = makeStyles({
  panel: {
    // Styled to read as the info MessageBars already on these screens, rather than being
    // one: a MessageBar is built for a sentence, and this carries a heading, an ordered
    // list and a control. Same visual family, correct semantics.
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    justifyContent: 'space-between',
  },
  heading: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
  },
  icon: { color: tokens.colorBrandForeground1, flexShrink: 0 },
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  steps: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    margin: 0,
    paddingLeft: tokens.spacingHorizontalXXL,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  whatYouNeed: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  whatYouNeedLabel: { fontWeight: tokens.fontWeightSemibold },
});

/**
 * The in-context instructions panel (CONVERSIONS_SPEC.md §6, slice C2).
 *
 * <p><b>Audience, stated in the spec and worth restating here:</b> a bookkeeper who has used
 * Xero, has never seen this tool, and will not read a wiki. That is why the panel is
 * <b>expanded by default</b>, never auto-hides on a timer, and remembers a dismissal
 * <b>per screen</b> rather than globally — dismissing the journals panel says nothing about
 * whether someone knows the uploads screen.</p>
 *
 * <p>Deliberately thin. Every rule it obeys lives in `lib/instructions.ts` and is tested
 * there. <b>Corrected 2026-08-18:</b> this docblock used to end that sentence with <i>"because
 * this repo's vitest runs in a node environment with no jsdom — logic placed in this file would
 * be logic no test can reach"</i>. <b>jsdom has been available since 2026-07-31</b>, and the
 * panel is now rendered for real in `Instructions.render.test.tsx`. The split is still right —
 * pure logic belongs in the lib — but it is a design choice, no longer a limitation.</p>
 *
 * <p>Per-field hints stay on `<Field hint>`: this panel explains the <i>task</i>, a hint
 * explains the <i>field</i>.</p>
 */
export default function Instructions({ screen }: { screen: InstructionScreen }) {
  const styles = useStyles();
  const { t } = useTranslation();
  const reactId = useId();
  const headingId = `instructions-heading-${reactId}`;
  const bodyId = `instructions-body-${reactId}`;

  // Read once on mount. The stored value is a user preference, not shared state — nothing
  // else writes it, so there is nothing to stay in sync with.
  const [collapsed, setCollapsed] = useState(() => readCollapsed(browserStorage(), screen));

  const toggle = useCallback(() => {
    setCollapsed((wasCollapsed) => {
      const next = !wasCollapsed;
      writeCollapsed(browserStorage(), screen, next);
      return next;
    });
  }, [screen]);

  // `returnObjects` gives the object behind `steps`: numbered keys ("1", "2", …), not an array, so
  // the fleet parity gate (tools/i18n) checks every step in en against es — it refuses arrays.
  // `stepsInOrder` sorts numerically. A non-object means the key is missing in every locale
  // including the `en` fallback — `instructions.content.test.ts` is what makes that unreachable,
  // so rendering nothing here is a second line, not the control.
  const steps = stepsInOrder(t(`instructions.${screen}.steps`, { returnObjects: true }));

  return (
    <section className={styles.panel} aria-labelledby={headingId}>
      <div className={styles.headerRow}>
        <Text as="h2" id={headingId} className={styles.heading}>
          <InfoRegular className={styles.icon} aria-hidden="true" />
          {t(`instructions.${screen}.title`)}
        </Text>
        <Button
          appearance="subtle"
          size="small"
          onClick={toggle}
          aria-expanded={!collapsed}
          aria-controls={bodyId}
          icon={collapsed ? <ChevronRightRegular /> : <ChevronDownRegular />}
        >
          {collapsed ? t('instructions.showLabel') : t('instructions.hideLabel')}
        </Button>
      </div>

      {/*
        Unmounted rather than hidden with CSS while collapsed: a collapsed panel must not
        leave its steps reachable by screen reader or by find-in-page, which is the same
        thing as not being collapsed for the people most likely to be relying on it.
      */}
      {!collapsed && (
        <div id={bodyId} className={styles.body}>
          <ol className={styles.steps}>
            {steps.map((step, i) => (
              // Index keys are correct here: this list is static per locale, never
              // reordered and never edited.
              <li key={i}>{step}</li>
            ))}
          </ol>
          <Text className={styles.whatYouNeed}>
            <span className={styles.whatYouNeedLabel}>{t('instructions.whatYouNeedLabel')}: </span>
            {t(`instructions.${screen}.whatYouNeed`)}
          </Text>
        </div>
      )}
    </section>
  );
}
